import { useEffect, useRef } from 'react';
import { logger } from '@/services/debug-logger';

/**
 * Hook to track component render cycles, mounts, and unmounts for debugging
 * @param componentName - Name of the component for logging
 * @param props - Optional props to log (be mindful of sensitive data)
 */
export function useDebugRender(componentName: string, props?: any) {
  const renderCount = useRef(0);
  const mountedAt = useRef<number>(Date.now());

  // Track renders
  renderCount.current += 1;
  logger.logComponentRender(componentName, renderCount.current, props);

  // Track mount and unmount
  useEffect(() => {
    logger.logComponentMount(componentName, props);

    return () => {
      const lifetime = Date.now() - mountedAt.current;
      logger.logComponentUnmount(componentName);
      logger.debug('LIFECYCLE', `${componentName} lifetime: ${lifetime}ms`);
    };
  }, [componentName]);
}

/**
 * Hook to track window/dialog open and close events
 * @param windowName - Name of the window/dialog
 * @param isOpen - Boolean indicating if the window is open
 */
export function useDebugWindow(windowName: string, isOpen: boolean) {
  const wasOpen = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      logger.logWindowEvent('opened', windowName);
    } else if (!isOpen && wasOpen.current) {
      logger.logWindowEvent('closed', windowName);
    }
    wasOpen.current = isOpen;
  }, [isOpen, windowName]);
}

/**
 * Hook to track view refreshes when dependencies change
 * @param viewName - Name of the view
 * @param dependencies - Dependencies that trigger refresh
 * @param reason - Optional reason for the refresh
 */
export function useDebugViewRefresh(viewName: string, dependencies: any[], reason?: string) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    logger.logViewRefresh(viewName, reason, { dependencies });
  }, dependencies);
}
